import { useEffect, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import ReactPlayer from 'react-player';

// MUI Imports
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import VolumeOffIcon from '@mui/icons-material/VolumeOff';
import { styled } from '@mui/material/styles';

// Project components
import AskForEmailPermissionPopup from 'components/miscellaneous/AskForEmailPermissionPopupProps';
import { NewsletterPopupActivator } from 'hooks/Newsletter';
import Navigation from './components/navigation/Navigation';
import { useAuth } from 'hooks/Auth';
import URLS from 'URLS';
import BookSchoolSubscriptionMeetingButton from 'containers/Landing/components/ActionButtons/BookSchoolSubscriptionMeetingButton';
import OrderSchoolSubscriptionButton from 'containers/Landing/components/ActionButtons/OrderSchoolSubscriptionButton';
import { schoolCtaFontSizeSx } from 'containers/Landing/components/ActionButtons/styles';
import DemoWithSelector from 'containers/Landing/components/DemoWithSelector';
import Poster from 'containers/Landing/components/Poster';
import UkuleleBanner from 'containers/Landing/components/UkuleleBanner';
import QaAccordion from 'containers/Landing/components/QaAccordion';
import YouTubeShorts, { DEFAULT_SHORT_URLS } from 'components/layout/YoutubeShorts';
import CalendlyBookMeeting, { CalendlyBookMeetingRef } from 'components/miscellaneous/CalendlyBookMeeting';

const SalesPitchHeaderText = styled(Typography)(({ theme }) => ({
  fontWeight: 'bold',
  textAlign: 'center',
  wordWrap: 'break-word',
  color: 'white',
  marginBottom: theme.spacing(3),
  fontSize: '1.5rem',
  [theme.breakpoints.up(450)]: {
    whiteSpace: 'pre-line',
  },
  [theme.breakpoints.up('sm')]: {
    fontSize: '1.7rem',
  },
  [theme.breakpoints.up('md')]: {
    fontSize: '1.7rem',
  },
  [theme.breakpoints.up('lg')]: {
    fontSize: '2.375rem',
  },
  [theme.breakpoints.up('xl')]: {
    fontSize: '2.8125rem',
  },
}));

const SalesPitchText = styled(Typography)(({ theme }) => ({
  color: 'white',
  marginBottom: theme.spacing(3),
  fontSize: '0.875rem',
  whiteSpace: 'pre-line',
  [theme.breakpoints.up('sm')]: {
    fontSize: '1rem',
  },
  [theme.breakpoints.up('md')]: {
    fontSize: '1.125rem',
  },
  [theme.breakpoints.up('lg')]: {
    fontSize: '1.25rem',
  },
  [theme.breakpoints.up('xl')]: {
    fontSize: '1.5625rem',
  },
}));

const ShortsHeader = styled(Typography)(({ theme }) => ({
  width: '100%',
  textAlign: 'center',
  paddingLeft: theme.spacing(2),
  paddingRight: theme.spacing(2),
  fontSize: '1.25rem',
  [theme.breakpoints.up('sm')]: {
    fontSize: '1.5rem',
  },
  [theme.breakpoints.up('md')]: {
    fontSize: '1.75rem',
  },
  [theme.breakpoints.up('lg')]: {
    fontSize: '2rem',
  },
  [theme.breakpoints.up('xl')]: {
    fontSize: '2.25rem',
  },
}));

const CalendlyHeader = styled(Typography)(({ theme }) => ({
  paddingLeft: theme.spacing(2),
  paddingRight: theme.spacing(2),
  fontSize: '1.125rem',
  whiteSpace: 'pre-line',
  textAlign: 'center',
  [theme.breakpoints.up('sm')]: {
    fontSize: '1.25rem',
  },
  [theme.breakpoints.up('md')]: {
    fontSize: '1.5rem',
  },
  [theme.breakpoints.up('lg')]: {
    fontSize: '1.75rem',
  },
  [theme.breakpoints.up('xl')]: {
    fontSize: '2rem',
  },
}));

const Landing = () => {
  const { t } = useTranslation(['common']);
  const navigate = useNavigate();
  const [auth, isAuthLoading] = useAuth();
  const [shouldAutoPlayVideo, setShouldAutoPlayVideo] = useState(true);
  const [isAutoMuted, setIsAutoMuted] = useState(true);
  const [isPlaying, setIsPlaying] = useState(false);
  const hasAutoplayed = useRef(false);
  const videoContainerRef = useRef<HTMLDivElement | null>(null);
  const playerRef = useRef<ReactPlayer | null>(null);

  useEffect(() => {
    if (window.history.state.idx === 0 && auth) {
      navigate(URLS.HUB);
    }
  }, [auth, isAuthLoading]);

  useEffect(() => {
    const videoContainer = videoContainerRef.current;

    if (!videoContainer) {
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) {
          setShouldAutoPlayVideo(false);
        }
      },
      {
        threshold: 0,
      },
    );

    observer.observe(videoContainer);

    return () => {
      observer.disconnect();
    };
  }, []);

  const unmutePlayer = () => {
    const internalPlayer = playerRef.current?.getInternalPlayer() as HTMLVideoElement | undefined;
    if (internalPlayer) {
      internalPlayer.muted = false;
      internalPlayer.volume = 1;
    }
    setIsAutoMuted(false);
  };

  const onPlayerReady = () => {
    const internalPlayer = playerRef.current?.getInternalPlayer() as HTMLVideoElement | undefined;
    if (!internalPlayer) {
      return;
    }
    internalPlayer.addEventListener('volumechange', () => {
      if (!internalPlayer.muted) {
        setIsAutoMuted(false);
      }
    });
  };

  const onVideoPlay = () => {
    setIsPlaying(true);
    if (!hasAutoplayed.current) {
      hasAutoplayed.current = true;
      return;
    }
    if (isAutoMuted) {
      unmutePlayer();
    }
  };

  const calendlyRef = useRef<CalendlyBookMeetingRef | null>(null);

  const onClick = () => {
    if (calendlyRef.current) {
      calendlyRef.current.focusCalendar();
    }
  };

  const videoUrl =
    'https://firebasestorage.googleapis.com/v0/b/ec-play-prod.appspot.com/o/landing%2Fvideos%2FEC-Play%20Landing%20video%2005-2026.mp4?alt=media&token=bfc97a2a-961b-4ad8-b3c9-a435a9e3124b';

  const shorts = DEFAULT_SHORT_URLS.map((url) => ({ url }));

  const QAs = [
    { question: t('LANDING.QA.ONE.QUESTION'), answer: t('LANDING.QA.ONE.ANSWER') },
    { question: t('LANDING.QA.TWO.QUESTION'), answer: t('LANDING.QA.TWO.ANSWER') },
    { question: t('LANDING.QA.THREE.QUESTION'), answer: t('LANDING.QA.THREE.ANSWER') },
  ];

  return (
    <>
      <AskForEmailPermissionPopup>
        <Navigation>
          <NewsletterPopupActivator />
          <UkuleleBanner />
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexDirection: 'column' }}>
            <Box
              sx={(theme) => ({
                display: 'flex',
                flexDirection: 'column',
                width: '100%',
                alignItems: 'center',
                background: '#141414',
                mb: 5,
                [theme.breakpoints.down('xl')]: { mb: 0 },
              })}>
              <Box
                sx={(theme) => ({
                  textAlign: 'center',
                  justifyContent: 'center',
                  display: 'flex',
                  width: '90%',
                  [theme.breakpoints.down('xl')]: { width: '100%', maxWidth: '100%' },
                })}>
                <Box
                  sx={(theme) => ({
                    display: 'flex',
                    justifyContent: 'center',
                    flexDirection: 'column',
                    p: 4,
                    width: '100%',
                    [theme.breakpoints.down('lg')]: { pt: 4, px: 2, pb: 4 },
                  })}>
                  <SalesPitchHeaderText variant='h3'>
                    <Trans i18nKey='LANDING.HOME.SALESHEADER' t={t} />
                  </SalesPitchHeaderText>
                  <SalesPitchText>
                    <Trans i18nKey='LANDING.HOME.SALESTEXT' t={t} />
                  </SalesPitchText>
                </Box>
              </Box>
              <Box
                ref={videoContainerRef}
                sx={(theme) => ({
                  position: 'relative',
                  width: '50%',
                  aspectRatio: '16/9',
                  mb: -5,
                  maxWidth: '800px',
                  [theme.breakpoints.down('xl')]: { width: '100%', mb: 0, maxWidth: '100%' },
                })}>
                <ReactPlayer
                  controls
                  height='100%'
                  muted
                  onPause={() => setIsPlaying(false)}
                  onPlay={onVideoPlay}
                  onReady={onPlayerReady}
                  playing={shouldAutoPlayVideo}
                  ref={playerRef}
                  url={videoUrl}
                  width='100%'
                />
                {isAutoMuted && isPlaying && (
                  <Button
                    onClick={unmutePlayer}
                    startIcon={<VolumeOffIcon />}
                    sx={{
                      position: 'absolute',
                      bottom: 1,
                      left: 1,
                      zIndex: 10,
                      background: 'rgba(0, 0, 0, 0.7)',
                      color: 'white',
                      '&:hover': { background: 'rgba(0, 0, 0, 0.9)' },
                    }}
                    variant='contained'>
                    {t('LANDING.HOME.TAPUNMUTE')}
                  </Button>
                )}
              </Box>
            </Box>
            <Box sx={{ py: 2 }}>
              <BookSchoolSubscriptionMeetingButton keepDefaultColor={true} onClick={onClick} sx={schoolCtaFontSizeSx} />
            </Box>
            <Box sx={{ width: '100%', maxWidth: '1000px' }}>
              <Poster text={t('LANDING.HOME.POSTERTEXT.ONE')} />
              <br />
              <Poster header={t('LANDING.HOME.POSTERHEADER.TWO')} text={t('LANDING.HOME.POSTERTEXT.TWO')} />
            </Box>
            <DemoWithSelector />
            <Box sx={{ width: '100%', pt: 2 }}>
              <ShortsHeader variant={'h2'}>{t('LANDING.HOME.SHORTSHEADER')}</ShortsHeader>
              <Box sx={{ width: '100%', background: '#141414' }}>
                <YouTubeShorts shorts={shorts} />
              </Box>
            </Box>
            <Box sx={{ py: 2 }}>
              <BookSchoolSubscriptionMeetingButton keepDefaultColor={true} onClick={onClick} sx={schoolCtaFontSizeSx} />
            </Box>
            <CalendlyHeader>{t('LANDING.CALENDLY.HEADER')}</CalendlyHeader>
            <CalendlyBookMeeting ref={calendlyRef} />
            <Box sx={{ width: '100%', maxWidth: '800px' }}>
              <QaAccordion QAs={QAs} />
            </Box>
            <Box sx={{ py: 2 }}>
              <OrderSchoolSubscriptionButton keepDefaultColor={true} sx={schoolCtaFontSizeSx} />
            </Box>
          </Box>
        </Navigation>
      </AskForEmailPermissionPopup>
    </>
  );
};

export default Landing;
